// Agent context menu rebuilt on shadcn (Sprint UI-9).
//
// Desktop: shadcn DropdownMenu at {x, y} viewport coordinates.
// Mobile: shadcn Sheet (bottom slide-up) with agent name header.
//
// Sections (D30, extended by sprint agent-stages):
//   1. Modifier — Mark read/unread, Rename, Fork
//   2. Stage — "Mover a estado": one item per stage (color dot + check on
//      the current one) + "Sin estado" + "Nuevo estado…" + "Gestionar
//      estados…". Hidden for archived agents (they rejoin a stage on
//      restore). Pin/Unpin retired per spec agent-stages D3.
//   3. Status — Archive / Restore
//   4. Remove — Delete (destructive)

import type { Agent } from '../types';
import { MD_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import { useTranslation } from '../i18n';
import { useStagesStore } from '../stores/stages.store';
import { isPaletteId, paletteColor } from '../lib/avatar-defaults';
import { Icon } from './atoms/Icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Sheet, SheetContent } from './ui/sheet';

export interface AgentContextMenuProps {
  agent: Agent;
  position: { x: number; y: number } | null;
  onClose: () => void;

  onMarkRead: () => void;
  onMarkUnread: () => void;
  onSetStage: (stageId: string | null) => void;
  onNewStage: () => void;
  onManageStages: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onRename: () => void;
  onDelete: () => void;
  onFork?: () => void;
}

interface Item {
  key: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  // Sprint agent-stages — stage rows render a color dot; the current
  // stage renders a trailing check. `hollow` marks "Sin estado".
  dotColorHex?: string;
  hollowDot?: boolean;
  checked?: boolean;
  testid: string;
}

interface Section {
  label?: string;
  items: Item[];
}

export function AgentContextMenu(props: AgentContextMenuProps) {
  const isDesktop = useMediaQuery(MD_QUERY);
  const { t } = useTranslation();
  const stages = useStagesStore((s) => s.stages);
  const sections = buildSections(props, stages, t);

  if (isDesktop) {
    return (
      <DesktopMenu
        position={props.position}
        onClose={props.onClose}
        sections={sections}
      />
    );
  }
  return (
    <MobileMenu
      onClose={props.onClose}
      sections={sections}
      agent={props.agent}
    />
  );
}

function buildSections(
  p: AgentContextMenuProps,
  stages: ReturnType<typeof useStagesStore.getState>['stages'],
  t: (key: import('../i18n').TKey) => string,
): Section[] {
  const inArchived = p.agent.archived;

  const modifier: Item[] = [];
  modifier.push(
    p.agent.unread
      ? {
          key: 'mark-read',
          label: t('contextMenu.markRead'),
          onClick: p.onMarkRead,
          testid: 'menu-item-mark-read',
        }
      : {
          key: 'mark-unread',
          label: t('contextMenu.markUnread'),
          onClick: p.onMarkUnread,
          testid: 'menu-item-mark-unread',
        },
  );
  modifier.push({
    key: 'rename',
    label: t('contextMenu.rename'),
    onClick: p.onRename,
    testid: 'menu-item-rename',
  });
  if (p.onFork && p.agent.claudeSessionId) {
    modifier.push({
      key: 'fork',
      label: t('contextMenu.fork'),
      onClick: p.onFork,
      testid: 'menu-item-fork',
    });
  }

  const sections: Section[] = [{ items: modifier }];

  if (!inArchived) {
    const stageItems: Item[] = stages.map((s) => ({
      key: `stage-${s.id}`,
      label: s.name,
      onClick: () => p.onSetStage(s.id),
      dotColorHex: isPaletteId(s.color) ? paletteColor(s.color).bg : undefined,
      checked: p.agent.stageId === s.id,
      testid: `menu-item-stage-${s.id}`,
    }));
    stageItems.push({
      key: 'stage-none',
      label: t('stages.noStage'),
      onClick: () => p.onSetStage(null),
      hollowDot: true,
      checked: p.agent.stageId == null || !stages.some((s) => s.id === p.agent.stageId),
      testid: 'menu-item-stage-none',
    });
    stageItems.push({
      key: 'stage-new',
      label: t('stages.newStage'),
      onClick: p.onNewStage,
      testid: 'menu-item-stage-new',
    });
    stageItems.push({
      key: 'stage-manage',
      label: t('stages.manage'),
      onClick: p.onManageStages,
      testid: 'menu-item-stage-manage',
    });
    sections.push({ label: t('stages.moveTo'), items: stageItems });
  }

  sections.push({
    items: [
      inArchived
        ? {
            key: 'restore',
            label: t('contextMenu.restore'),
            onClick: p.onRestore,
            testid: 'menu-item-restore',
          }
        : {
            key: 'archive',
            label: t('contextMenu.archive'),
            onClick: p.onArchive,
            testid: 'menu-item-archive',
          },
    ],
  });

  sections.push({
    items: [
      {
        key: 'delete',
        label: t('contextMenu.delete'),
        onClick: p.onDelete,
        destructive: true,
        testid: 'menu-item-delete',
      },
    ],
  });

  return sections;
}

// Shared dot renderer — 8px stage color dot, hollow variant for "Sin
// estado". Hex values mirror the `avatar.palette-NN` tokens (see
// lib/avatar-defaults.ts header for the sanctioned-duplication note).
function StageDot({ hex, hollow }: { hex?: string; hollow?: boolean }) {
  if (!hex && !hollow) return null;
  return (
    <span
      className={[
        'inline-block h-2 w-2 rounded-full shrink-0',
        hollow ? 'border border-muted-foreground/60' : '',
      ].join(' ')}
      style={hex ? { backgroundColor: hex } : undefined}
      aria-hidden="true"
    />
  );
}

function DesktopMenu({
  position,
  onClose,
  sections,
}: {
  position: { x: number; y: number } | null;
  onClose: () => void;
  sections: Section[];
}) {
  const x = position?.x ?? 0;
  const y = position?.y ?? 0;

  return (
    <DropdownMenu open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DropdownMenuContent
        align="start"
        side="bottom"
        className="min-w-[180px]"
        style={{ position: 'fixed', left: x, top: y }}
        data-testid="agent-context-menu"
      >
        {sections.map((section, si) => (
          <SectionItems key={si} section={section} onClose={onClose} showSeparator={si > 0} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu({
  onClose,
  sections,
  agent,
}: {
  onClose: () => void;
  sections: Section[];
  agent: Agent;
}) {
  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="bottom"
        className="rounded-t-xl pb-safe-bottom p-0"
        data-testid="agent-context-menu"
      >
        <div className="px-4 pt-4 pb-2">
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-muted-foreground/30" aria-hidden="true" />
          <p className="text-xs uppercase tracking-wide text-muted-foreground truncate">
            {agent.name}
          </p>
        </div>
        <div className="py-1 max-h-96 overflow-y-auto">
          {sections.map((section, si) => (
            <MobileSectionItems key={si} section={section} onClose={onClose} showSeparator={si > 0} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SectionItems({
  section,
  onClose,
  showSeparator,
}: {
  section: Section;
  onClose: () => void;
  showSeparator: boolean;
}) {
  return (
    <>
      {showSeparator && <DropdownMenuSeparator data-testid="menu-section-divider" />}
      {section.label && (
        <DropdownMenuLabel className="text-caption uppercase tracking-wide text-muted-foreground/70">
          {section.label}
        </DropdownMenuLabel>
      )}
      {section.items.map((it) => (
        <DropdownMenuItem
          key={it.key}
          onSelect={() => {
            it.onClick();
            onClose();
          }}
          className={it.destructive ? 'text-destructive focus:text-destructive' : undefined}
          data-testid={it.testid}
        >
          <StageDot hex={it.dotColorHex} hollow={it.hollowDot} />
          <span className="flex-1">{it.label}</span>
          {it.checked && <Icon name="Check" size={13} aria-hidden="true" />}
        </DropdownMenuItem>
      ))}
    </>
  );
}

function MobileSectionItems({
  section,
  onClose,
  showSeparator,
}: {
  section: Section;
  onClose: () => void;
  showSeparator: boolean;
}) {
  return (
    <>
      {showSeparator && (
        <div className="mx-4 my-1 border-t border-border" aria-hidden="true" data-testid="menu-section-divider" />
      )}
      {section.label && (
        <p className="px-4 pt-2 pb-1 text-xs uppercase tracking-wide text-muted-foreground/70">
          {section.label}
        </p>
      )}
      {section.items.map((it) => (
        <button
          key={it.key}
          type="button"
          onClick={() => {
            it.onClick();
            onClose();
          }}
          className={`w-full text-left px-4 py-3 text-sm hover:bg-accent flex items-center gap-2.5 ${
            it.destructive ? 'text-destructive' : 'text-foreground'
          }`}
          style={{ minHeight: 48 }}
          data-testid={it.testid}
        >
          <StageDot hex={it.dotColorHex} hollow={it.hollowDot} />
          <span className="flex-1">{it.label}</span>
          {it.checked && <Icon name="Check" size={14} aria-hidden="true" />}
        </button>
      ))}
    </>
  );
}
